import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  CreditCard,
  Check,
  Star,
  Zap,
  RefreshCw,
  Calendar,
  Clock,
  ArrowRight,
  Receipt,
  TrendingUp
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

export default function Billing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingCycle, setBillingCycle] = useState("monthly");
  
  // Check for return from Stripe
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    loadData();
    
    // Check payment status if returning from Stripe
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const loadData = async () => {
    setLoading(true);
    const token = localStorage.getItem("speakr_token");
    
    try {
      const [subRes, plansRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/billing/subscription/${user?.business_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/settings/plans`),
        fetch(`${API_URL}/api/billing/history/${user?.business_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (subRes.ok) {
        setSubscription(await subRes.json());
      }
      if (plansRes.ok) {
        setPlans(await plansRes.json());
      }
      if (historyRes.ok) {
        const historyData = await historyRes.json();
        setBillingHistory(historyData.history || []);
      }
    } catch (error) {
      console.error("Failed to load billing data:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    const token = localStorage.getItem("speakr_token");
    let attempts = 0;
    const maxAttempts = 5;
    
    const pollStatus = async () => {
      if (attempts >= maxAttempts) {
        toast.info("Payment is being processed. Check your email for confirmation.");
        return;
      }
      
      attempts++;
      
      try {
        const res = await fetch(`${API_URL}/api/billing/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          
          if (data.payment_status === "paid") {
            toast.success("Payment successful! Your subscription is now active.");
            loadData();
            // Clear the session_id from URL
            navigate("/dashboard/billing", { replace: true });
            return;
          } else if (data.status === "expired") {
            toast.error("Payment session expired. Please try again.");
            navigate("/dashboard/billing", { replace: true });
            return;
          }
        }
        
        // Continue polling
        setTimeout(pollStatus, 2000);
      } catch (error) {
        console.error("Error checking payment status:", error);
      }
    };
    
    pollStatus();
  };

  const subscribeToPlan = async (planId) => {
    if (planId === "free_trial") {
      toast.info("You're already on the free plan");
      return;
    }
    
    setProcessing(true);
    const token = localStorage.getItem("speakr_token");
    
    try {
      const res = await fetch(`${API_URL}/api/billing/checkout/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: planId,
          billing_cycle: billingCycle,
          origin_url: window.location.origin,
          customer_id: user?.business_id,
          customer_email: user?.email
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create checkout session");
      }
    } catch (error) {
      toast.error("Failed to initiate checkout");
    } finally {
      setProcessing(false);
    }
  };

  const openBillingPortal = async () => {
    const token = localStorage.getItem("speakr_token");
    
    try {
      const res = await fetch(`${API_URL}/api/billing/portal?return_url=${encodeURIComponent(window.location.href)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.portal_url;
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to open billing portal");
      }
    } catch (error) {
      toast.error("Failed to open billing portal");
    }
  };

  const getPrice = (plan) => {
    if (billingCycle === "yearly" && plan.price_yearly) {
      return plan.price_yearly;
    }
    return plan.price_monthly;
  };

  const getMonthlySavings = (plan) => {
    if (billingCycle === "yearly" && plan.price_yearly && plan.price_monthly) {
      const yearlyMonthly = plan.price_yearly / 12;
      return Math.round((plan.price_monthly - yearlyMonthly) / plan.price_monthly * 100);
    }
    return 0;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Billing & Subscription</h1>
        <p className="text-slate-500 mt-1">Manage your subscription and view billing history</p>
      </div>

      {/* Current Subscription */}
      {subscription && (
        <Card data-testid="current-subscription-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Current Plan</CardTitle>
                  <CardDescription>Your active subscription</CardDescription>
                </div>
              </div>
              <Badge className={subscription.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                {subscription.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold capitalize">{subscription.plan_name?.replace(/_/g, " ")}</h3>
                <p className="text-slate-500">
                  ${subscription.price}/{subscription.billing_cycle === "yearly" ? "year" : "month"}
                </p>
              </div>
              <div className="text-right text-sm text-slate-500">
                {subscription.ends_at && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>Renews: {new Date(subscription.ends_at).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
          <CardFooter className="border-t pt-4">
            <Button
              variant="outline"
              onClick={openBillingPortal}
              className="w-full"
              data-testid="manage-billing-btn"
            >
              <Receipt className="w-4 h-4 mr-2" />
              Manage Billing & Invoices
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Billing Cycle Toggle */}
      <div className="flex items-center justify-center gap-4 p-4 bg-slate-50 rounded-lg">
        <Button
          variant={billingCycle === "monthly" ? "default" : "outline"}
          onClick={() => setBillingCycle("monthly")}
          data-testid="monthly-toggle"
        >
          Monthly
        </Button>
        <Button
          variant={billingCycle === "yearly" ? "default" : "outline"}
          onClick={() => setBillingCycle("yearly")}
          className="relative"
          data-testid="yearly-toggle"
        >
          Yearly
          <Badge className="absolute -top-2 -right-2 bg-emerald-500 text-xs">
            Save 20%
          </Badge>
        </Button>
      </div>

      {/* Available Plans */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.filter(p => p.is_active !== false).map((plan) => {
            const isCurrentPlan = subscription?.plan_id === (plan.id || plan.slug);
            const price = getPrice(plan);
            const savings = getMonthlySavings(plan);
            
            return (
              <Card 
                key={plan.id || plan.slug}
                className={`relative ${plan.is_featured ? "ring-2 ring-blue-500" : ""} ${isCurrentPlan ? "bg-blue-50" : ""}`}
                data-testid={`plan-card-${plan.slug}`}
              >
                {plan.is_featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-blue-500">
                      <Star className="w-3 h-3 mr-1" fill="currentColor" /> Popular
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{plan.name}</CardTitle>
                  <div className="pt-2">
                    <span className="text-3xl font-bold">${price}</span>
                    <span className="text-slate-500">/{billingCycle === "yearly" ? "year" : "month"}</span>
                    {savings > 0 && (
                      <Badge variant="outline" className="ml-2 text-emerald-600">
                        Save {savings}%
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-3">
                  {plan.description && (
                    <p className="text-sm text-slate-600">{plan.description}</p>
                  )}
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span>{plan.max_agents === -1 ? "Unlimited" : plan.max_agents} Agents</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span>{plan.max_minutes_monthly === -1 ? "Unlimited" : plan.max_minutes_monthly} min/month</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span>{plan.max_contacts === -1 ? "Unlimited" : plan.max_contacts} Contacts</span>
                    </div>
                  </div>
                  
                  {plan.features && plan.features.length > 0 && (
                    <div className="pt-2 border-t space-y-1">
                      {plan.features.slice(0, 3).map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          <Zap className="w-3 h-3 text-blue-500 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
                
                <CardFooter>
                  {isCurrentPlan ? (
                    <Button className="w-full" disabled variant="outline">
                      Current Plan
                    </Button>
                  ) : price === 0 ? (
                    <Button className="w-full" variant="outline" disabled>
                      Free Plan
                    </Button>
                  ) : (
                    <Button 
                      className="w-full"
                      onClick={() => subscribeToPlan(plan.id || plan.slug)}
                      disabled={processing}
                      data-testid={`subscribe-${plan.slug}`}
                    >
                      {processing ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ArrowRight className="w-4 h-4 mr-2" />
                      )}
                      {subscription?.plan_id && subscription.price > 0 ? "Change Plan" : "Subscribe"}
                    </Button>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Billing History */}
      {billingHistory.length > 0 && (
        <Card data-testid="billing-history-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Receipt className="w-5 h-5 text-slate-600" />
              <CardTitle>Billing History</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {billingHistory.map((item, idx) => (
                <div 
                  key={item.id || idx}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-2 h-2 rounded-full ${
                      item.payment_status === "paid" ? "bg-emerald-500" : 
                      item.payment_status === "failed" ? "bg-red-500" : "bg-amber-500"
                    }`} />
                    <div>
                      <p className="font-medium capitalize">{item.plan_id?.replace(/_/g, " ")}</p>
                      <p className="text-sm text-slate-500">
                        {new Date(item.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">${item.amount} {item.currency?.toUpperCase()}</p>
                    <Badge variant="outline" className={
                      item.payment_status === "paid" ? "text-emerald-600" : 
                      item.payment_status === "failed" ? "text-red-600" : "text-amber-600"
                    }>
                      {item.payment_status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
